import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { Plus, Filter, Download, Eye, Edit, Search, AlertTriangle, Lightbulb, Mail, X, Save, CheckCircle2 } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
const initialNCData = Array.from({
  length: 50
}, (_, i) => ({
  id: String(i + 1),
  number: `NC-2024/${String(i + 1).padStart(3, '0')}`,
  type: i % 3 === 0 ? 'Interna' : i % 3 === 1 ? 'Cliente' : 'Fornitore',
  description: 'Descrizione non conformità...',
  date: '2024-05-10',
  responsible: i % 3 === 0 ? 'Lorenzo' : i % 3 === 1 ? 'Alessio' : 'Gian Franco',
  status: i % 2 === 0 ? 'Aperta' : 'Chiusa'
}));
const initialImprovementsData = Array.from({
  length: 20
}, (_, i) => ({
  id: String(i + 1),
  number: `MIG-2024/${String(i + 1).padStart(3, '0')}`,
  description: 'Proposta miglioramento processo...',
  date: '2024-05-15',
  status: 'In valutazione',
  responsible: i % 2 === 0 ? 'Alessio' : 'Lorenzo'
}));
const ncColumnsConfig = [{
  key: 'number',
  label: 'Numero'
}, {
  key: 'type',
  label: 'Tipologia'
}, {
  key: 'description',
  label: 'Descrizione'
}, {
  key: 'date',
  label: 'Data rilev.'
}, {
  key: 'responsible',
  label: 'Responsabile'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function NonConformitiesPage() {
  const [activeTab, setActiveTab] = useState<'nc' | 'improvements'>('nc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(ncColumnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialNCData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['number', 'description']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    if (activeTab === 'nc') {
      const nextNumber = `NC-2024/${String(initialNCData.length + 1).padStart(3, '0')}`;
      setFormData({
        number: nextNumber,
        status: 'Aperta'
      });
    } else {
      const nextNumber = `MIG-2024/${String(initialImprovementsData.length + 1).padStart(3, '0')}`;
      setFormData({
        number: nextNumber,
        status: 'In valutazione'
      });
    }
    setIsModalOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', activeTab === 'nc' ? 'Non conformità salvata' : 'Miglioramento salvato', 'I dati sono stati salvati correttamente.');
    }, 1500);
  };
  const handleSendReport = () => {
    addNotification('success', 'Report inviato', 'Il report NC è stato inviato al cliente.');
    setIsReportModalOpen(false);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  return <Layout data-id="element-2320">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2321">
        <div data-id="element-2322">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2323">
            Non conformità e miglioramenti
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2324">
            Gestione NC, reclami e piani di miglioramento.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2325">
          <div className="bg-slate-100 p-1 rounded-xl flex items-center" data-id="element-2326">
            <button onClick={() => setActiveTab('nc')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'nc' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2327">
              Non conformità
            </button>
            <button onClick={() => setActiveTab('improvements')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'improvements' ? 'bg-white text-seform-navy shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-2328">
              Miglioramenti
            </button>
          </div>
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2330" />} data-id="element-2329">
            Esporta
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2332" />} data-id="element-2331">
            {activeTab === 'nc' ? 'Non conformità' : 'Nuovo miglioramento'}
          </Button>
        </div>
      </div>

      {activeTab === 'nc' ? <>
          <Card className="mb-6" noPadding data-id="element-2333">
            <div className="p-4 flex flex-col gap-4" data-id="element-2334">
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-2335">
                <div className="flex-1" data-id="element-2336">
                  <Input placeholder="Cerca non conformità..." startContent={<Search className="h-4 w-4" data-id="element-2338" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2337" />
                </div>
                <div className="flex gap-2" data-id="element-2339">
                  <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2341" />} data-id="element-2340">
                    Filtri{' '}
                    {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2342">
                        {Object.keys(filters).filter(k => filters[k]).length}
                      </Badge>}
                  </Button>
                  <ColumnCustomizer columns={ncColumnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="nc" data-id="element-2343" />
                </div>
              </div>

              {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2344">
                  <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
              value: '',
              label: 'Tutti'
            }, {
              value: 'Aperta',
              label: 'Aperta'
            }, {
              value: 'Chiusa',
              label: 'Chiusa'
            }]} data-id="element-2345" />
                  <Select label="Tipologia" value={filters.type || ''} onChange={e => setFilter('type', e.target.value)} options={[{
              value: '',
              label: 'Tutte'
            }, {
              value: 'Interna',
              label: 'Interna'
            }, {
              value: 'Cliente',
              label: 'Cliente'
            }, {
              value: 'Fornitore',
              label: 'Fornitore'
            }]} data-id="element-2346" />
                  <div className="col-span-full flex justify-end" data-id="element-2347">
                    <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2349" />} data-id="element-2348">
                      Resetta filtri
                    </Button>
                  </div>
                </div>}
            </div>
          </Card>

          <Card noPadding className="overflow-hidden" data-id="element-2350">
            <div className="overflow-x-auto" data-id="element-2351">
              <table className="w-full text-left border-collapse table-fixed" data-id="element-2352">
                <thead data-id="element-2353">
                  <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2354">
                    {ncColumnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-right w-28' : ''}`} data-id="element-2355">
                          {col.label}
                        </th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-2356">
                  {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2357">
                      {visibleColumns.includes('number') && <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-2358">
                          {item.number}
                        </td>}
                      {visibleColumns.includes('type') && <td className="px-6 py-4" data-id="element-2359">
                          <Badge variant={item.type === 'Interna' ? 'info' : item.type === 'Cliente' ? 'warning' : 'neutral'} data-id="element-2360">
                            {item.type}
                          </Badge>
                        </td>}
                      {visibleColumns.includes('description') && <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate" data-id="element-2361">
                          {item.description}
                        </td>}
                      {visibleColumns.includes('date') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2362">
                          {item.date}
                        </td>}
                      {visibleColumns.includes('responsible') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2363">
                          {item.responsible}
                        </td>}
                      {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-2364">
                          <Badge variant={item.status === 'Aperta' ? 'danger' : 'success'} data-id="element-2365">
                            {item.status}
                          </Badge>
                        </td>}
                      {visibleColumns.includes('actions') && <td className="px-6 py-4 text-right" data-id="element-2366">
                          <div className="flex items-center justify-end gap-2" data-id="element-2367">
                            <Button variant="light" size="sm" className="!px-2" onClick={() => setIsReportModalOpen(true)} data-id="element-2368">
                              <Mail className="h-4 w-4 text-slate-500" data-id="element-2369" />
                            </Button>
                            <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-2370">
                              <Edit className="h-4 w-4 text-seform-orange" data-id="element-2371" />
                            </Button>
                          </div>
                        </td>}
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100" data-id="element-2372">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2373" />
            </div>
          </Card>
        </> : <Card noPadding className="overflow-hidden" data-id="element-2374">
          <div className="p-4 border-b border-slate-100" data-id="element-2375">
            <h3 className="font-bold text-seform-navy" data-id="element-2376">Elenco miglioramenti</h3>
          </div>
          <div className="overflow-x-auto" data-id="element-2377">
            <table className="w-full text-left border-collapse table-fixed" data-id="element-2378">
              <thead data-id="element-2379">
                <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2380">
                  <th className="px-6 py-4" data-id="element-2381">Numero</th>
                  <th className="px-6 py-4" data-id="element-2382">Descrizione</th>
                  <th className="px-6 py-4" data-id="element-2383">Data proposta</th>
                  <th className="px-6 py-4" data-id="element-2384">Responsabile</th>
                  <th className="px-6 py-4" data-id="element-2385">Stato</th>
                  <th className="px-6 py-4 text-right" data-id="element-2386">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-2387">
                {initialImprovementsData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2388">
                    <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-2389">
                      {item.number}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2390">
                      {item.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2391">
                      {item.date}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2392">
                      {item.responsible}
                    </td>
                    <td className="px-6 py-4" data-id="element-2393">
                      <Badge variant="info" data-id="element-2394">{item.status}</Badge>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-2395">
                      <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-2396">
                        <Edit className="h-4 w-4 text-seform-orange" data-id="element-2397" />
                      </Button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </Card>}

      {/* Create/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? activeTab === 'nc' ? 'Modifica non conformità' : 'Modifica miglioramento' : activeTab === 'nc' ? 'Nuova non conformità' : 'Nuovo miglioramento'} size="2xl" data-id="element-2398">
        <AnimatePresence mode="wait" data-id="element-2399">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2400">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2401">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2402" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2403">
                {activeTab === 'nc' ? 'Non conformità salvata con successo' : 'Miglioramento salvato con successo'}
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2404">
              <div className="space-y-8" data-id="element-2405">
                {activeTab === 'nc' ? <>
                    <section data-id="element-2406">
                      <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2407">
                        <AlertTriangle className="h-4 w-4" data-id="element-2408" /> Dettagli non
                        conformità
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2409">
                        <Input label="Numero" value={formData.number || ''} onChange={e => setFormData({
                    ...formData,
                    number: e.target.value
                  })} placeholder="" disabled={!!selectedItem} data-id="element-2410" />
                        <Select label="Tipologia" value={formData.type || ''} onChange={e => setFormData({
                    ...formData,
                    type: e.target.value
                  })} options={[{
                    value: 'Interna',
                    label: 'Interna'
                  }, {
                    value: 'Cliente',
                    label: 'Cliente'
                  }, {
                    value: 'Fornitore',
                    label: 'Fornitore'
                  }]} required data-id="element-2411" />
                        <DatePicker label="Data rilevazione" value={formData.date || ''} onChange={e => setFormData({
                    ...formData,
                    date: e.target.value
                  })} data-id="element-2412" />
                        <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                    ...formData,
                    responsible: e.target.value
                  })} placeholder="" data-id="element-2413" />
                        <div className="col-span-full" data-id="element-2414">
                          <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                      ...formData,
                      description: e.target.value
                    })} placeholder="" required data-id="element-2415" />
                        </div>
                      </div>
                    </section>

                    <section data-id="element-2416">
                      <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2417">
                        <Lightbulb className="h-4 w-4" data-id="element-2418" /> Azione correttiva
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2419">
                        <div className="col-span-full" data-id="element-2420">
                          <Textarea label="Descrizione azione" value={formData.actionDescription || ''} onChange={e => setFormData({
                      ...formData,
                      actionDescription: e.target.value
                    })} placeholder="" data-id="element-2421" />
                        </div>
                        <Input label="Responsabile azione" value={formData.actionResponsible || ''} onChange={e => setFormData({
                    ...formData,
                    actionResponsible: e.target.value
                  })} placeholder="" data-id="element-2422" />
                        <DatePicker label="Scadenza azione" value={formData.actionDeadline || ''} onChange={e => setFormData({
                    ...formData,
                    actionDeadline: e.target.value
                  })} data-id="element-2423" />
                        <Select label="Stato" value={formData.status || 'Aperta'} onChange={e => setFormData({
                    ...formData,
                    status: e.target.value
                  })} options={[{
                    value: 'Aperta',
                    label: 'Aperta'
                  }, {
                    value: 'Sospesa',
                    label: 'Sospesa'
                  }, {
                    value: 'Chiusa',
                    label: 'Chiusa'
                  }]} required data-id="element-2424" />
                      </div>
                    </section>
                  </> : <section data-id="element-2425">
                    <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-2426">
                      <Lightbulb className="h-4 w-4" data-id="element-2427" /> Dettagli miglioramento
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2428">
                      <Input label="Numero" value={formData.number || ''} onChange={e => setFormData({
                  ...formData,
                  number: e.target.value
                })} placeholder="" disabled={!!selectedItem} data-id="element-2429" />
                      <DatePicker label="Data proposta" value={formData.date || ''} onChange={e => setFormData({
                  ...formData,
                  date: e.target.value
                })} data-id="element-2430" />
                      <div className="col-span-full" data-id="element-2431">
                        <Textarea label="Descrizione" value={formData.description || ''} onChange={e => setFormData({
                    ...formData,
                    description: e.target.value
                  })} placeholder="" required data-id="element-2432" />
                      </div>
                      <Input label="Responsabile" value={formData.responsible || ''} onChange={e => setFormData({
                  ...formData,
                  responsible: e.target.value
                })} placeholder="" data-id="element-2433" />
                      <Select label="Stato" value={formData.status || 'In valutazione'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={[{
                  value: 'In valutazione',
                  label: 'In valutazione'
                }, {
                  value: 'Aperta',
                  label: 'Aperta'
                }, {
                  value: 'Chiusa',
                  label: 'Chiusa'
                }, {
                  value: 'Scartata',
                  label: 'Scartata'
                }]} required data-id="element-2434" />
                    </div>
                  </section>}

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2435">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-2436">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2438" />} data-id="element-2437">
                    Salva
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      {/* Report Modal */}
      <Modal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} title="Invia report NC" size="md" data-id="element-2439">
        <div className="space-y-6" data-id="element-2440">
          <p className="text-sm text-slate-600" data-id="element-2441">
            Stai per inviare il report della non conformità al cliente via
            email.
          </p>
          <Input label="Email destinatario" defaultValue="cliente@email.it" data-id="element-2442" />
          <Input label="Oggetto" defaultValue="Report Non Conformità #NC-2024/001" data-id="element-2443" />
          <Textarea label="Messaggio" defaultValue="Gentile Cliente, in allegato il report della NC rilevata..." data-id="element-2444" />

          <div className="bg-slate-50 p-3 rounded border border-slate-200 flex items-center gap-2 text-sm text-slate-600" data-id="element-2445">
            <Download className="h-4 w-4" data-id="element-2446" /> report_nc_001.pdf
          </div>

          <div className="flex justify-end gap-3 pt-4" data-id="element-2447">
            <Button variant="flat" color="default" onClick={() => setIsReportModalOpen(false)} data-id="element-2448">
              Annulla
            </Button>
            <Button onClick={handleSendReport} startContent={<Mail className="h-4 w-4" data-id="element-2450" />} data-id="element-2449">
              Invia report
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>;
}